import {Component, OnDestroy, OnInit, Signal, WritableSignal} from '@angular/core';
import { SlicePipe } from '@angular/common';
import {ApiRequestService} from "../../services/api-request.service";
import {Event} from '../../model/event';
import {format, differenceInDays, setHours, setMinutes, setSeconds, subMonths, addMonths, addDays, lastDayOfMonth, isBefore, isAfter, isEqual, parseISO} from 'date-fns';
import {CalendarDay} from 'src/app/model/calendarDay';
import {Category} from "../../model/category";
import {TimelinePreference} from 'src/app/model/timelinePreference';
import {CalendarData} from 'src/app/model/calendarData';
import {UserService} from "../../services/user.service";
import {MetricsService} from "../../services/metrics.service";
import {AppConfigService} from "../../app-config.service";
import {Router} from "@angular/router";
import {lineSpinner} from 'ldrs';
// Required for RJXS
import { Subject } from 'rxjs';
import {MsalAuthService} from "../../services/msal-auth.service";
import {StudentEnrollmentService} from "../../services/student-enrollment.service";
import {environment} from "../../../environments/environment";
/**
 * ### Calendar Component
 *
 * Creates and displays main page calendar widget.
 */
@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    standalone: false
})
export class CalendarComponent implements OnInit, OnDestroy {

    /**
     * Label used for Calendar header
     */
    public days_labels: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    /**
     * Marker for the currently shown month.
     */
    public currentMonthYear = {
        current_date: new Date(),
        month: new Date().getMonth(),
        year: new Date().getFullYear()
    };
    /**
     * Current focused date
     * @type {Date}
     */
    public targetDate = new Date();

    public today = new Date();
    /**
     * Full list of categories available
     * @type {array}
     */
    public calendarCategories: Array<Category> = [];
    /**
     * Array of user preferences indicating categories opted out.
     */
    public userPrefs: Array<TimelinePreference> = [];
    /**
     * Populate the calendar with a 2D array which is populated in O(n) time
     * @type {CalendarDay}
     */
    public calendarList: CalendarDay[][] = [];


    /**
     * IsLoaded flag to indicate fetch completion
     * @type {boolean}
     */
    public isLoaded: boolean = true;
    /**
     * Calendar title. Month Year
     * @type {string}
     */
    public title = '';
    /**
     * Raw event data from API
     * @type {Array<Event>}
     */
    public eventList: any = [];
    /**
     * Parsed and processed event data suitable for display
     * @type {Array<Event>}
     */
    public eventData = new Map<string, Event[]>();
    public outlookEventData = new Map<string, Event[]>();
    public classEventData = new Map<string, Event[]>();
    /**
     * Flag to include next day events. Should be true if showing today.
     * @type {boolean}
     */
    public includeTomorrow: boolean = false;
    /**
     * Loading flag for saving preferences
     * @type {boolean}
     */
    public prefLoading: boolean = false;
    /**
     * Failed flag for saving preferences
     * @type {boolean}
     */
    public prefFailed: boolean = false;
    /**
     * Flag to try saving again
     */
    public prefTryAgain: boolean = false;
    /**
     * Success flag for saving preferences
     * @type {boolean}
     */
    public prefSuccess: boolean = false;

    public countDownEvents: any = [];

    /**
     * Controls Hover events on Calendar Month controls.
     */
    public caretLeftHover: boolean = false;
    public caretRightHover: boolean = false;
    private processCountDownEvents: boolean = environment.feature_CountdownEvents;
    private countDownLimitInDays: number = 6;

    public enableO365Integration: boolean = environment.feature_O365;
    public enableClassScheduleIntegration: boolean = environment.feature_studentEnrollment && this.userService.hasRole("student");

    public tokenExpiration: string = '';
    // public showMSLogin: boolean = true;
    private readonly _destroying$ = new Subject<void>();

    /**
     * Component Constructor
     * Injects API Service
     * @param apiService
     * @param userService
     * @param metricService
     * @param appConfig
     * @param router
     * @param msal
     * @param enrollment
     */
    constructor(
        private apiService: ApiRequestService,
        private userService: UserService,
        private metricService: MetricsService,
        private appConfig: AppConfigService,
        private router: Router,
        private msal: MsalAuthService,
        private enrollment: StudentEnrollmentService
    ) {
    }

    /**
     * On component Init Method
     * Triggers API fetch call for event data
     */
    ngOnInit(): void {
        let me = this;
        lineSpinner.register();
        if ( this.userService.hasAccessToken() ) {
            me.populateCalendarModels(me.currentMonthYear);
            // me.processCountDownEvents = false;
            performance.clearMarks("calendarStart");
            performance.clearMarks("calendarComplete");
            performance.clearMeasures("calendarMeasure");
            performance.mark("calendarStart");
            me.fetchEvents();
        }
        if ( this.isMSLoggedIn === true ) {
            me.fetchMSEvents();
        } else {
            this.msal.hasActiveAccount$.subscribe({
                next: (resp:boolean) => {
                    if ( resp === true ) {
                        me.fetchMSEvents();
                    }
                }
            });
        }
        this.enrollment.myClasses$.subscribe({
            next: () => {
                this.getTodaysClasses();
                this.populateCalendarModels(this.currentMonthYear);
            }
        })
        // if ( this.userService.hasRole("student") ) {
        //     this.toggleClassesOptin();
        // }
    }
    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

/* ******************* */
    public login() {
        this.msal.login();
    }
    public logout() {
        this.msal.logout();
    }
    async fetchMSEvents(): Promise<void> {
        // let me = this;
        let tdate = new Date(this.targetDate);
        let firstDate = format( new Date(tdate.getFullYear(), tdate.getMonth(), 1), "yyyy-MM-dd'T00:00:00'xxx" );
        let lastDate = format( new Date(tdate.getFullYear(), tdate.getMonth(), lastDayOfMonth(tdate).getDate()), "yyyy-MM-dd'T23:59:59'xxx" );
        this.outlookEventData = new Map<string, Event[]>();
        let events = await this.msal.getMyEvents(firstDate,lastDate);
        if ( events.length > 0 ) {
            events.forEach( (event:any) => {
                let evt: Event = {};
                let date = new Date(event.start.dateTime);
                let index = format(date,"yyyy-MM-dd");
                evt.summary = event.subject;
                evt.description = "";
                evt.url = event.webLink;
                evt.isAllDay = event.isAllDay;
                evt.color = "red";
                evt.source = "My Outlook";
                evt.catId = null;
                evt.collapsed = true;
                evt._startTime = date.getTime();
                evt.startTime = format(date,"h:mm aaa");
                if (this.outlookEventData.get(index) == undefined) {
                    this.outlookEventData.set(index, new Array());
                }
                this.outlookEventData.get(index)!.push(evt);
            } );
        }
        this.populateCalendarModels(this.currentMonthYear);
    }
    public toggleO365Optin() {
        if ( this.isMSLoggedIn ) {
            this.logout();
        } else {
            this.login();
        }
        if (document.getElementById("calendarsettings-label").getAttribute("aria-expanded") == 'true')
            document.getElementById("calendarsettings-label").click();
    }
    public get isMSLoggedIn(): boolean {
        return this.msal.getHasActiveAccount();
    }
    public hasOutlookEvents(): boolean {
        return this.outlookEventData.has( format(this.targetDate, "yyyy-MM-dd") );
    }
    public getOutlookEvents() {
        let events: Event[] = this.outlookEventData.get(format(this.targetDate, "yyyy-MM-dd"));
        if (events != undefined && events.length > 0) {
            events.sort((a, b) => a._startTime - b._startTime);
        }
        return events;
    }
/* ******************* */

    /**
     * Used mainly so we can spy on and mock this during testing.
     */
    public getTodayDate() {
        return this.today;
    }

    public populateCalendarModels(params: any) {

        // console.log(this.outlookEventData);
        var months_labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

        var days_in_month = getDaysInMonth(params.month, params.year),
            first_day_date = new Date(params.year, params.month, 1),
            first_day_weekday = first_day_date.getDay();

        var prev_month = params.month == 0 ? 11 : params.month - 1,
            prev_year = prev_month == 11 ? params.year - 1 : params.year,
            prev_days = getDaysInMonth(prev_month, prev_year);

        // calendar header
        this.title = months_labels[params.month] + ' ' + params.year;

        function getDaysInMonth(month: any, year: any) {
            // 0 = last day of the previous month
            return new Date(year, month + 1, 0).getDate();
        }

        // week days labels
        for (var i = 0; i <= 6; i++) {
            this.days_labels[i];
        }

        var w = 0; // week day
        var n = 1; // next days date
        var c = 1; // current date
        let me = this;
        me.calendarList = [];
        // dates loop
        var calendarWeek: CalendarDay[] = [];
        for (var i = 0; i < 6 * this.days_labels.length; i++) {

            //Create Day
            var calendarDay: CalendarDay = {};
            calendarDay.date_stamp = "";
            calendarDay.calendar_day = 0;
            calendarDay.title = "";
            calendarDay.in_month = false;
            calendarDay.is_active = false;
            calendarDay.is_today = false;
            calendarDay.is_selected = false;

            if (w == 0) {
                // first week's day
                // clear array
                calendarWeek = [];
            }

            if (i < new Date(params.year, params.month, 1).getDay()) {
                // previous month's day
                let day = prev_days - first_day_weekday + i + 1;
                calendarDay.calendar_day = day;
                let display_date = subMonths( new Date(params.year, params.month, day), 1 );
                let display_date_class = format(display_date, "yyyy-MM-dd");
                calendarDay.date_stamp = display_date_class;
            } else if (c > days_in_month) {
                // next month's day
                calendarDay.calendar_day = n;
                let display_date = addMonths(new Date(params.year, params.month, n), 1 );
                let display_date_class = format(display_date, "yyyy-MM-dd");
                calendarDay.date_stamp = display_date_class;
                n++;
            } else {
                // current month's day
                // let day:calendarDay =
                let options = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'} as const;
                let display_date = new Date(params.year, params.month, c);
                let display_date_class = format(display_date, "yyyy-MM-dd");
                let has_event = me.hasEventDataByOptIn( me.eventData.get(display_date_class) ); // != undefined;
                let has_outlook = me.outlookEventData.has(display_date_class);
                let isToday = (format(display_date, "yyyy-MM-dd") == format(this.getTodayDate(), "yyyy-MM-dd"));
                let isSelected = (format(me.targetDate, "yyyy-MM-dd") == format(display_date, "yyyy-MM-dd"));
                let title = display_date.toLocaleDateString("en-GB", options);
                let has_class_event = me.hasClassEvent(display_date);
                // let hasClass: boolean = me.

                calendarDay.title = title;
                calendarDay.date_stamp = display_date_class;
                calendarDay.calendar_day = c;
                calendarDay.is_today = isToday;
                calendarDay.is_active = isToday;
                calendarDay.is_selected = isSelected;
                calendarDay.has_event = has_event;
                calendarDay.has_outlook = has_outlook;
                calendarDay.has_classes = has_class_event;
                calendarDay.in_month = true;
                // @ts-ignore
                c++;
            }
            calendarWeek.push(calendarDay);

            if (w == this.days_labels.length - 1) {
                // last week's day
                // push to array
                this.calendarList.push(calendarWeek);
                w = 0;
            } else {
                w++;
            }
            me.getTodaysClasses();
        }
    }

    private hasEventDataByOptIn( events: Event[] ): boolean {
        if ( events == null || events.length == 0 ) {
            return false;
        }
        let me = this;
        let hasEvents: boolean = false;
        events.forEach( (e:Event) => {
            if ( !me.isCategoryOptOut( e.catId ) ) {
                hasEvents = true
            }
        });
        return hasEvents;
    }
    /**
     * Method to format target date for display.
     * Should show 'Today' if todays date.
     */
    public displayTargetDate() {
        if (this.isToday()) {
            return "Today, " + format(this.targetDate, "LLL d")
        } else {
            return format(this.targetDate, "EEEE, LLL d")
        }
    }

    public displayTomorrow() {
        return "Tomorrow, " + format(this.getTomorrow(), "LLL d");
    }

    /**
     * Method to find if targetdate has events present in data
     */
    public hasEvents() {
        return this.hasEventDataByOptIn( this.eventData.get(format(this.targetDate, "yyyy-MM-dd") ) );
        // return (this.eventData.get(format(this.targetDate, "yyyy-MM-dd")) != undefined);
    }

    /**
     * Returns target Dates events from data array
     */
    public getEvents() {
        let events: Event[] = this.eventData.get(format(this.targetDate, "yyyy-MM-dd"));
        if (events != undefined && events.length > 0) {
            events.sort((a, b) => a._startTime - b._startTime);
        }
        return events;
    }

    /**
     * Checks for if selected is today
     * @returns True if it is, false otherwise.
     */
    public isToday() {
        return format(this.getTodayDate(), "yyyy-MM-dd") == format(this.targetDate, "yyyy-MM-dd") ? true : false;
    }

    /**
     * Gets the date of tomorrow
     * @returns A date consisting of today plus one day
     */
    public getTomorrow() {
        let tomorrow = new Date(this.getTodayDate().getTime());
        tomorrow.setDate(this.getTodayDate().getDate() + 1);
        return tomorrow;
    }

    /**
     * Checks to see if tomorrow has events
     * @returns True if event data has an event for tomorrow, false otherwise
     */
    public hasTomorrowEvents() {
        return (this.eventData.get(format(this.getTomorrow(), "yyyy-MM-dd")) != undefined);
    }

    /**
     * Returns target tomorrow events from data array
     */
    public getTomorrowEvents() {
        let tomorrow = this.getTomorrow();

        let events = this.eventData.get(format(tomorrow, "yyyy-MM-dd"));
        if (events != undefined && events.length > 0) {
            events.sort((a, b) => a._startTime - b._startTime);
        }
        return events;
    }

    /**
     * Main method to process and parse data from API.
     * Converts to a single level array for display.
     * @private
     */
    public processEventList() {
        let me = this;
        // let today = new Date();
        if (me.eventList.data.length > 0) {
            me.eventList.data.forEach((source: any) => { // For each Feed...
                me.assignColor(source.id, source.color);
                if (source.ical != undefined && source.ical.length == undefined) {
                    let feedName = me.getCategoryName(source.id);
                    for (let d in source.ical) {
                        source.ical[d].forEach((event: Event) => { // For each event...
                            let index = d.toString();
                            event.color = source.color;
                            event.source = feedName;
                            event.catId = source.id;
                            event.collapsed = true;
                            let date = new Date(event.startDate);
                            event._startTime = date.getTime();
                            event.startTime = format(date,"h:mm aaa");
                            if (me.eventData.get(index) == undefined) {
                                me.eventData.set(index, new Array());
                            }
                            //
                            if ( !me.isCategoryOptOut( source.id ) && !me.eventData.get(index).some( (e:Event) => e.url == event.url ) ) {
                                me.eventData.get(index)!.push(event);
                            }
                            // Capture Countdown Events HERE.
                            if ( me.processCountDownEvents && event.countdown == true && me.hasCountDownAccess(event) ) {
                                let earlierDate: Date = setSeconds( setMinutes( setHours( me.getTodayDate(), 0 ), 0), 0);
                                let laterDate: Date = setSeconds( setMinutes( setHours( date, 0 ), 0), 0);
                                let diff = ( differenceInDays( laterDate, earlierDate ) + 1 );
                                if ( format(me.getTodayDate(),"yyyy-MM-dd") == format(date,"yyyy-MM-dd") ) {
                                    diff = 0;
                                }
                                if ( diff > 0 && diff <= me.countDownLimitInDays ) {
                                    let newevent = { days: diff, link: event.url, title: event.summary, text: diff+" Day"+(diff==1?"":"s"), class: "bg-secondary text-white" };
                                    if ( diff == 0 ) {
                                        newevent.text = "TODAY";
                                        newevent.class = "text-black bg-warning";
                                    }
                                    if ( diff == 1 ) {
                                        newevent.class = "text-black bg-warning";
                                    }
                                    me.countDownEvents.push( newevent );
                                }
                            }
                        });
                    }
                }
            });
        }
        me.countDownEvents.sort((a: any, b: any) => a.days - b.days);
        me.processCountDownEvents = false;
        // console.log( "countDownEvents EVENTS: ", me.countDownEvents );
    }

    private hasCountDownAccess( event: Event ): boolean {
        let me = this;
        if ( event.countdownGroups == "" ) {
            return true;
        } else {
            let roles = event.countdownGroups.split(';');
            let hasAccess: boolean = false;
            roles.forEach( (r: string) => {
                if ( me.userService.hasRole( r.toLowerCase() ) ) {
                    hasAccess = true;
                }
            });
            return hasAccess;
        }
    }
    /**
     * Sets color from api payload to calendarCategories object.
     * Used to show color of category in dropdown
     * @param catId
     * @param color
     * @private
     */
    private assignColor(catId: number, color: string) {
        for (let i = 0; i < this.calendarCategories.length; i++) {
            if (this.calendarCategories[i].unquieCategoryId == catId) {
                this.calendarCategories[i].color = color;
            }
        }
    }

    /**
     * Return category name given ID
     * @param id
     * @private
     */
    private getCategoryName(id: number) {
        let ret = "";
        this.calendarCategories.forEach((cat) => {
            if (cat.unquieCategoryId == id) ret = cat.name;
        });
        return ret;
    }

    /**
     * Method to trigger fetch from API
     * @private
     */
    public fetchEvents() {
        let me = this;
        // let calendarKey = me.currentMonthYear.month + me.currentMonthYear.year;
        me.eventList = [];
        me.eventData = new Map<string, Event[]>();
        me.populateCalendarModels(me.currentMonthYear);
        me.isLoaded = false;
        let firstDate = this.calendarList[0][0].date_stamp;
        let lastDate = format( addDays( new Date(this.calendarList[ this.calendarList.length-1 ][ 6 ].date_stamp), this.countDownLimitInDays ), "yyyy-MM-dd" );
        this.apiService.getEvents(firstDate,lastDate).subscribe({
            next: (resp: CalendarData) => {
                // me.countDownEvents = [];
                me.eventList = resp;
                me.isLoaded = true;
                me.calendarCategories = resp.categories;
                me.processEventList();

                if (resp.eventPrefs.length > 0) {
                    resp.eventPrefs.forEach((p: TimelinePreference) => {
                        me.calendarCategories.forEach((c: Category) => {
                            if (p.categoryId == c.unquieCategoryId)
                                c.optout = true;
                        });
                    });
                }
                // console.log( resp.classPrefs );
                if ( resp.classPrefs !== false ) {
                    me.myClassesOptIn = true;
                    me.enrollment.setOptIn(me.myClassesOptIn);
                    if ( me.enrollment.myClasses() == null && this.enableClassScheduleIntegration ) {
                        me.enrollment.processClasses();
                    }
                }
                performance.mark('calendarComplete');
                performance.measure('calendarMeasure', 'calendarStart', 'calendarComplete');
                me.metricService.logMetric( { name: "calendarMeasure Load Time (ms)", value: performance.getEntriesByName("calendarMeasure")[0].duration } );
            },
            error: (error) => {
                me.isLoaded = true;
                me.metricService.sendError("CALENDAR FAILED ("+me.userService.getNsid()+") - " + error.status + " - " + error.error.message );
                if( error.status == 401 && me.userService.isTokenExpired() && !me.userService.isRTexpired() ) {
                    me.router.navigate(["/login"]);
                }
            },
            complete: () => {
                me.isLoaded = true;
                me.populateCalendarModels(me.currentMonthYear);
            }
        });
    }
    private isCategoryOptOut( catId: number ): boolean {
        let isOptOut: boolean = false;
        // console.log( this.calendarCategories );
        this.calendarCategories.forEach((c: Category) => {
            if ( c.id == catId && c.optout == true ) {
                isOptOut = true;
            }
        });
        return isOptOut;
    }

    public getCategoryHeadings() {
        let headings: Category[] = [];
        if ( this.calendarCategories.length > 0 ) {
            this.calendarCategories.forEach( (c:Category) => {
                if ( c.parent == 0 ) {
                    headings.push( c );
                }
            } );
        }
        return headings;
    }
    public getCategories( parent: number ) {
        let categories: Category[] = [];
        if ( this.calendarCategories.length > 0 ) {
            this.calendarCategories.forEach( (c:Category) => {
                if ( c.parent == parent ) {
                    categories.push( c );
                }
            } );
        }
        return categories;
    }
    /**
     * Toggles the collapsed state of an calendar event.
     * @param item
     */
    public toggleCollapsed(item: any) {
        if (item.hasOwnProperty('collapsed')) {
            if (item.collapsed) {
                this.metricService.sendCalendarEventView( item.summary );
            }
            item.collapsed = !item.collapsed;
        }
    }

    public sendGACountdownEvent( title: string ) {
        this.metricService.sendCountdownEventClick( title );
    }
    /**
     * Populates Event list when a date is clicked.
     * @param date
     */
    public loadEventOnClick(date: any) {
        let slashedDate: string = date.replace(/-/g, '\/');
        let convertedDate: Date = new Date(slashedDate);
        let me = this;
        me.targetDate = convertedDate;
        //There might be a better way to optimize this
        me.populateCalendarModels(me.currentMonthYear);
    }

    public setToday() {
        let today = this.getTodayDate();
        this.currentMonthYear = {
            current_date: today,
            month: today.getMonth(),
            year: today.getFullYear()
        };
        this.targetDate = today;
        this.targetDate.setDate(today.getDate());
        this.fetchEvents();
        this.populateCalendarModels(this.currentMonthYear);

    }
    /**
     * Sets new month. Used on month forward and back buttons are clicked.
     * @param inc
     */
    public setMonth(inc: number) {
        let me = this;
        let newMonth = me.currentMonthYear.current_date;
        newMonth.setMonth(newMonth.getMonth() + inc);
        me.currentMonthYear = {
            current_date: newMonth,
            month: newMonth.getMonth(),
            year: newMonth.getFullYear()
        };
        me.targetDate = newMonth;
        me.targetDate.setDate(1);
        me.fetchEvents();
        me.populateCalendarModels(me.currentMonthYear);
    }

    /**
     * Toggle prefernece optin / out on checkbox click.
     * @param cat
     */
    public togglePreference(cat: Category) {
        cat.optout = !cat.optout;
    }

    /**
     * Save preferences to API
     */
    public savePreferences() {
        let me = this;
        let params: Array<any> = [];
        this.prefLoading = true;
        this.prefSuccess = false;
        this.prefFailed = false;
        me.prefTryAgain = false;
        this.calendarCategories.forEach((cat) => {
            if (cat.optout) {
                params.push({
                    categoryId: cat.unquieCategoryId,
                    code: cat.code,
                    unquieCategoryId: (cat.unquieCategoryId != undefined ? cat.unquieCategoryId : 0),
                    source: cat.source
                });
            } //else if (cat.categoryId != undefined) params.push({categoryId: cat.id, unquieCategoryId: 0, source: cat.source});
        });
        // this.classes = [];

        params.push({ classes: this.myClassesOptIn });
        // if ( this.myClassesOptIn ) {
        //     params.push({
        //         classes: true,
        //     });
        // } else {
        //     this.enrollment.optIn = false;
        // }

        this.apiService.saveCalendarPreferences(params).subscribe({
            next: (resp) => {
                me.prefLoading = false;
                me.prefSuccess = true;
            }, error: (err: any) => {
                if (err.status == 401) {
                    if (me.userService.isTokenExpired()) {
                        me.userService.refreshUserToken();
                        me.prefLoading = false;
                        me.prefTryAgain = true;
                    } else {
                        me.prefLoading = false;
                        me.prefFailed = true;
                    }
                } else {
                    me.prefLoading = false;
                    me.prefFailed = true;
                }
            }, complete: () => {
                setTimeout(() => {
                    //console.log( 'closing ' );
                    me.prefLoading = false;
                    me.prefSuccess = false;
                    me.prefFailed = false;
                    if (document.getElementById("calendarsettings-label").getAttribute("aria-expanded") == 'true')
                        document.getElementById("calendarsettings-label").click();
                    me.fetchEvents();
                }, this.appConfig.getNotificationTimeoutInMiliSeconds());
            }
        });

    }

    /* ******************* */

    public showAllClassList: boolean = false;
    public toggleViewClassList() {
        this.showAllClassList = !this.showAllClassList;
    }
    public get classScheduleState(): string {
        return this.enrollment.classScheduleState();
    }
    public get isLoadingClasses(): boolean {
        return this.enrollment.classScheduleState() == 'loading';
    }
    public get showMyClassesOptIn(): boolean {
        return ( this.enableClassScheduleIntegration && this.userService.hasRole("student") );
    }
    public myClassesOptIn: boolean = true;
    public toggleClassesOptin(){
        this.myClassesOptIn = !this.myClassesOptIn;
        this.enrollment.setOptIn(this.myClassesOptIn);
        if ( this.enrollment.myClasses() == null && this.enableClassScheduleIntegration ) {
            this.enrollment.processClasses();
        }
    }
    public get classes() {
        if ( this.enrollment.myClasses() != null ) {
            return this.enrollment.myClasses();
        } else {
            return [];
        }
    }
    public todaysClasses: any[] = [];
    public getTodaysClasses(): void {
        if ( this.enrollment.myClasses() == null ) {
            return ;
        }
        let tomorrow = new Date(this.getTodayDate().getTime());
        tomorrow.setDate(this.getTodayDate().getDate() + 1);
        this.tomorrowsClasses = [];

        let me = this;
        let todayDay = format(this.targetDate, "eeee").toLowerCase();
        let ret: any[] = [];
        // console.log( "getTodaysClasses: " + todayDay );
        this.enrollment.myClasses().forEach((c:any) => {
            if ( c.courseSchedule.length > 0 ) {
                for ( let i = 0; i < c.courseSchedule.length; i++ ) {
                    let start = new Date( parseISO(c.courseSchedule[i].startDate+" "+c.courseSchedule[i].startTime) );
                    let end = new Date( parseISO(c.courseSchedule[i].endDate+" "+c.courseSchedule[i].endTime) );
                    if (
                        c.courseSchedule[i].daysOfWeek.includes( todayDay )
                        && format(start,'yyyy-MM-dd') <= format(me.targetDate,'yyyy-MM-dd')
                        && format(end,'yyyy-MM-dd') >= format(me.targetDate,'yyyy-MM-dd')
                    ) {
                        ret.push({
                            daysOfWeek: c.courseSchedule[i].daysOfWeek,
                            courseTime: c.courseSchedule[i].startTime,
                            courseCode: c.courseCode,
                            courseNumber: c.courseNumber,
                            courseName: c.courseName,
                            courseLocation: c.courseLocation,
                            instructionalMethods: c.instructionalMethods,
                            collapsed: true
                        });
                    }
                    if (
                        c.courseSchedule[i].daysOfWeek.includes( format(tomorrow, "eeee").toLowerCase() )
                        && format(start,'yyyy-MM-dd') <= format(tomorrow,'yyyy-MM-dd')
                        && format(end,'yyyy-MM-dd') >= format(tomorrow,'yyyy-MM-dd')
                    ) {
                        this.tomorrowsClasses.push({
                            daysOfWeek: c.courseSchedule[i].daysOfWeek,
                            courseTime: c.courseSchedule[i].startTime,
                            courseCode: c.courseCode,
                            courseNumber: c.courseNumber,
                            courseName: c.courseName,
                            courseLocation: c.courseLocation,
                            instructionalMethods: c.instructionalMethods,
                            collapsed: true
                        });
                    }
                }
            }
            ret.sort((a:any, b:any) => a.courseTime.localeCompare(b.courseTime));
        });
        this.todaysClasses = ret;
        // return ret;
    }
    public tomorrowsClasses: any[] = [];;;;


    public hasClassEvent( date: Date ) {
        let ret = false;
        if ( this.enableClassScheduleIntegration && this.enrollment.myClasses() != null ) {
            if ( this.enrollment.myClasses().length > 0 ) {
                this.enrollment.myClasses().forEach((c: any) => {
                    if (c.courseSchedule.length > 0) {
                        for (let i = 0; i < c.courseSchedule.length; i++) {
                            if (
                                c.courseSchedule[i].startDate <= format(date, 'yyyy-MM-dd')
                                && c.courseSchedule[i].endDate >= format(date, 'yyyy-MM-dd')
                                && c.courseSchedule[i].daysOfWeek.includes(format(date, 'eeee').toLowerCase())
                            ) {
                                ret = true;
                            }
                        }
                    }
                });
            }
        }
        return ret;
    }
}
